import { savedAccountsLimit } from "@saleor/components/AppLayout/consts";
import SavedAccountContext, {
  Account
} from "@saleor/components/AppLayout/SavedAccountContext";
import SwitchAccountContext from "@saleor/components/AppLayout/SwitchAccountContext";
import SwitchAccountLoader from "@saleor/components/SwitchAccountLoader/SwitchAccountLoader";
import useLocalStorage from "@saleor/hooks/useLocalStorage";
import useNotifier from "@saleor/hooks/useNotifier";
import RemoveAccountDialog from "@saleor/navigation/components/RemoveAccountDialog/RemoveAccountDialog";
import React, { useEffect, useState } from "react";

interface SavedAccountProviderProps {
  children: React.ReactNode;
}

const SavedAccountProvider: React.FC<SavedAccountProviderProps> = ({
  children
}) => {
  const notify = useNotifier();
  const [user, setUser] = useState(null);
  const [recentUser, setRecentUser] = useLocalStorage("recentUser", null);
  const [savedAccounts, setSavedAccounts] = useLocalStorage(
    "savedAccounts",
    []
  );
  const [ignoredAccounts, setIgnoredAccounts] = useLocalStorage(
    "ignoredAccounts",
    []
  );
  const [removeAccount, setRemoveAccount] = useState(null);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  useEffect(() => {
    document.body.style.overflow = switchingAccount ? "hidden" : "auto";
  }, [switchingAccount]);

  const [showSaveAccountDialog, setShowSaveAccountDialog] = useState(false);

  useEffect(() => {
    if (
      user !== undefined &&
      user !== null &&
      !savedAccounts.some(savedAccount => savedAccount.email === user.email) &&
      !ignoredAccounts.includes(user.email) &&
      savedAccounts.length < savedAccountsLimit
    ) {
      setShowSaveAccountDialog(true);
    } else {
      setShowSaveAccountDialog(false);
    }
  }, [savedAccounts, ignoredAccounts, user]);

  const handleRemoveAccount = () => {
    if (!removeAccount) {
      return;
    }
    const updatedSavedAccounts = savedAccounts.filter(
      savedAccount => savedAccount.email !== removeAccount
    );

    setSavedAccounts(updatedSavedAccounts);
    setRemoveAccount(null);
    notify({
      status: "success",
      text: "Account removed successfully!"
    });
  };

  const handleUpdateSavedAccount = (email: string, data) => {
    const updatedSavedAccounts = savedAccounts
      .map(savedAccount =>
        savedAccount.email === email
          ? { ...savedAccount, ...data }
          : savedAccount
      )
      .sort(
        (savedAccount, anotherSavedAccount) =>
          anotherSavedAccount.lastLogin - savedAccount.lastLogin
      );

    setSavedAccounts(updatedSavedAccounts);
  };

  const handleAddSavedAccount = (account: Account) => {
    if (
      savedAccounts.some(savedAccount => savedAccount.email === account.email)
    ) {
      return;
    }

    setSavedAccounts([account, ...savedAccounts]);
  };

  const logSavedAccount = (user, token, csrfToken) => {
    if (
      !savedAccounts.some(savedAccount => savedAccount.email === user.email)
    ) {
      return;
    }
    handleUpdateSavedAccount(user.email, {
      csrf: csrfToken,
      lastLogin: Date.now(),
      token
    });
  };

  const handleRemoveFromIgnoredAccounts = email => {
    if (!ignoredAccounts.includes(email)) {
      return;
    }
    setIgnoredAccounts(
      ignoredAccounts.filter(ignoredEmail => ignoredEmail !== email)
    );
  };

  return (
    <SavedAccountContext.Provider
      value={{
        handleAddSavedAccount,
        handleRemoveAccount,
        handleRemoveFromIgnoredAccounts,
        handleUpdateSavedAccount,
        ignoredAccounts,
        logSavedAccount,
        recentUser,
        removeAccount,
        savedAccounts,
        setIgnoredAccounts,
        setRecentUser,
        setRemoveAccount,
        setShowSaveAccountDialog,
        setUser,
        showSaveAccountDialog
      }}
    >
      <SwitchAccountContext.Provider
        value={{
          setSwitchingAccount,
          switchingAccount
        }}
      >
        {children}

        {switchingAccount ? <SwitchAccountLoader /> : null}
        {removeAccount ? <RemoveAccountDialog /> : null}
      </SwitchAccountContext.Provider>
    </SavedAccountContext.Provider>
  );
};

export default SavedAccountProvider;
